import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { format } from 'date-fns';
import { db } from '../config/firebase';
import { Auction } from '../types/auction.types';
import { DEFAULT_SCHEDULE } from '../services/bidding.service';

/** Public sale-terms page. No auth — T&Cs must be readable before registering. Reads only
 *  the auction doc (the same doc SalePage loads). Renders collapsible sections mirroring the
 *  HiBid layout; sections with no data are omitted entirely. */

const money = (cents: number) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const plain = (cents: number) => (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const toDate = (v: any): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v?.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const Section: React.FC<{ title: string; defaultOpen?: boolean; children: React.ReactNode }> = ({
  title,
  defaultOpen = true,
  children,
}) => (
  <details open={defaultOpen} className="card">
    <summary className="cursor-pointer text-xl font-bold list-none flex items-center justify-between">
      <span>{title}</span>
      <span className="text-gray-400 text-base">▾</span>
    </summary>
    <div className="mt-4">{children}</div>
  </details>
);

const Row: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) =>
  value == null || value === '' ? null : (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-gray-100 last:border-0">
      <div className="text-sm font-semibold text-gray-600">{label}</div>
      <div className="col-span-2 text-sm text-gray-800 whitespace-pre-line">{value}</div>
    </div>
  );

const SaleInfo: React.FC = () => {
  const { auctionId } = useParams<{ auctionId: string }>();
  const [auction, setAuction] = useState<Auction | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!auctionId) return;
      try {
        const snap = await getDoc(doc(db, 'auctions', auctionId));
        if (active) setAuction(snap.exists() ? ({ id: snap.id, ...(snap.data() as any) } as Auction) : null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [auctionId]);

  if (loading) return <div className="p-8 text-center text-gray-600">Loading sale info…</div>;
  if (!auction)
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold">Sale not found</h1>
        <Link to="/" className="text-primary-600 hover:underline">Back to auctions</Link>
      </div>
    );

  const info = auction.saleInfo || {};
  const start = toDate(auction.startDate);
  const end = toDate(auction.endDate);
  const datesText =
    start || end
      ? `${start ? format(start, 'MMM d, yyyy h:mm a') : '—'}  to  ${end ? format(end, 'MMM d, yyyy h:mm a') : '—'}`
      : undefined;

  // ① Auction Information — render only if at least one row has data.
  const hasAuctionInfo =
    auction.title || auction.auctionHouseName || info.auctionType || datesText ||
    info.dateTimeInfo || info.previewInfo || info.checkoutInfo || info.location ||
    info.buyersPremiumText || info.description;

  // ③ Bid increments — from the auction's own schedule (same data the engine settles from).
  const schedule = auction.bidIncrements ?? DEFAULT_SCHEDULE;
  const incrementRows = schedule.map((b, i) => {
    const lowerCents = i === 0 ? 0 : ((schedule[i - 1].upTo ?? 0) + 1);
    const range = b.upTo == null
      ? `${plain(lowerCents)} and up`
      : `${plain(lowerCents)} – ${plain(b.upTo)}`;
    return { range, inc: money(b.increment) };
  });

  const hasPayment = info.buyersPremiumText || info.paymentTerms;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-3xl font-bold">{auction.title} — Sale Info &amp; Terms</h1>
        <Link to={`/sale/${auction.id}`} className="btn-secondary">Back to Sale</Link>
      </div>

      {/* ① Auction Information */}
      {hasAuctionInfo && (
        <Section title="Auction Information">
          <Row label="Name" value={auction.title} />
          <Row label="Auctioneer" value={auction.auctionHouseName} />
          <Row label="Type" value={info.auctionType} />
          <Row label="Dates" value={datesText} />
          <Row label="Date / Time Info" value={info.dateTimeInfo} />
          <Row label="Preview" value={info.previewInfo} />
          <Row label="Checkout" value={info.checkoutInfo} />
          <Row label="Location" value={info.location} />
          <Row label="Buyer's Premium" value={info.buyersPremiumText} />
          {/* Auctioneer-defined cool-off / tally window (auction.coolOffWindow, seconds). */}
          <Row
            label="Cool-Off / Tally Window"
            value={
              auction.coolOffWindow
                ? `${auction.coolOffWindow % 3600 === 0
                    ? `${auction.coolOffWindow / 3600} hour${auction.coolOffWindow / 3600 === 1 ? '' : 's'}`
                    : `${Math.round(auction.coolOffWindow / 60)} minutes`} after the last lot closes — bids are tallied and confirmed before results are official.`
                : undefined
            }
          />
          <Row label="Description" value={info.description} />
        </Section>
      )}

      {/* ② Terms and Conditions */}
      {info.terms && info.terms.length > 0 && (
        <Section title="Terms and Conditions">
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-800">
            {info.terms.map((t, i) => (
              <li key={i} className="whitespace-pre-line">{t}</li>
            ))}
          </ol>
        </Section>
      )}

      {/* ③ Bid Increments — rendered FROM auction.bidIncrements (never hardcoded) */}
      {incrementRows.length > 0 && (
        <Section title="Bid Increments">
          <p className="text-xs text-gray-500 mb-3">
            These are the exact increments the auction uses to compute the next minimum bid.
          </p>
          <div className="divide-y divide-gray-100">
            {incrementRows.map((r, i) => (
              <div key={i} className="grid grid-cols-2 gap-3 py-1.5 text-sm">
                <span className="text-gray-700">{r.range}</span>
                <span className="font-semibold text-gray-900">→ {r.inc}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ④ Payment Information */}
      {hasPayment && (
        <Section title="Payment Information">
          <Row label="Currency" value="USD ($)" />
          <Row label="Buyer's Premium" value={info.buyersPremiumText} />
          <Row label="Payment Terms" value={info.paymentTerms} />
        </Section>
      )}

      {/* ⑤ Shipping / Pick Up */}
      {info.shippingPickup && (
        <Section title="Shipping / Pick Up">
          <p className="text-sm text-gray-800 whitespace-pre-line">{info.shippingPickup}</p>
        </Section>
      )}
    </div>
  );
};

export default SaleInfo;
